import "./EditableAvatar.scss"

import { ChangeEvent, useEffect, useRef, useState } from "react"

import ColoredLetter from "@/shared/components/extrinsic/ColoredLetter/ColoredLetter"
import { classWithModifiers } from "@/utils/common"
import useAsyncBoundary from "@/utils/hooks/useAsyncBoundary"
import FileUtils, { ImageResolution } from "@/utils/transform/file"

import Icon from "../../intrinsic/Icon/Icon"
import Loader from "../Loader/Loader"

interface EditableAvatarProps {
  name?: string
  defaultImage?: string
  firstName?: string
  onChange?(file: File): void | Promise<unknown>
  pending?: boolean

  /**
   * Will display image preview containing it instead of covering.
   */
  contain?: boolean

  /** In bytes. */
  maxSize?: number
  /** In pixels. */
  maxResolution?: [number, number]

  /**
   * If true, the cover will always be visible regardless of hover state.
   */
  showCover?: boolean

  letterColor?: string
  backgroundColor?: string
  baseHSL?: [number, number, number]
}

function EditableAvatar(props: EditableAvatarProps) {
  const [image, setImage] = useState<string | null>(props.defaultImage ?? null)
  const [{ pending }, asyncBoundary] = useAsyncBoundary()
  const letterRef = useRef<HTMLDivElement>(null)
  const [invalid, setInvalid] = useState(false)

  async function convertLetterToImage(): Promise<File | null> {
    if (!letterRef.current || !props.firstName) return null

    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")
    if (!ctx) return null

    canvas.width = 200
    canvas.height = 200

    const [h, s, l] = props.baseHSL ?? [255, 50, 50]
    const alphabet = "abcdefghijklmnopqrstuvwxyz".split("")
    const letterIndex = alphabet.indexOf(props.firstName.toLowerCase())
    const letterBackground = props.backgroundColor ?? `hsl(${ h / alphabet.length * letterIndex } ${ s }% ${ l }%)`

    ctx.fillStyle = letterBackground
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    ctx.fillStyle = props.letterColor || "#FFFFFF"
    ctx.font = "120px Arial"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText(props.firstName, canvas.width / 2, canvas.height / 2)

    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob)
      }, "image/png")
    })

    return new File([blob], "company-logo.png", { type: "image/png" })
  }

  useEffect(() => {
    if (!image && props.firstName && props.onChange) {
      convertLetterToImage().then(file => {
        if (file) {
          props.onChange?.(file)
        }
      })
    }
  }, [image, props.firstName, props.onChange])

  async function onChange(event: ChangeEvent<HTMLInputElement>) {
    const target = event.currentTarget

    // checks
    const files = target.files
    if(files === null) return

    const file = files[0] as File | undefined
    if(file == null) return

    setInvalid(false)

    if(exceedsMaxSize(file.size) || exceedsMaxResolution(await FileUtils.getImageResolution(file))) {
      setInvalid(true)
      // Empties the files.
      target.files = new DataTransfer().files
      return
    }

    // awaits
    if(props.onChange) {
      await asyncBoundary(() => props.onChange?.(file))
    }

    // updates
    if(!/^image\/(png|jpg|jpeg)$/.test(file.type)) return
    setImage(URL.createObjectURL(file))
  }

  function exceedsMaxSize(size: number): boolean {
    if(props.maxSize == null) return false

    return size > props.maxSize
  }
  function exceedsMaxResolution(resolution: ImageResolution): boolean {
    if(resolution.width > maxWidth) return true
    if(resolution.height > maxHeight) return true

    return false
  }

  function getErrorMessage() {
    if(props.maxSize != null && props.maxResolution == null) {
      return `Its size should not exceed ${ (props.maxSize ?? 0) / 1024 / 1024 }MB`
    }

    if(props.maxSize == null && props.maxResolution != null) {
      return `Its resolution should not exceed ${ maxWidth }x${ maxHeight }px`
    }

    return `Its size should not exceed ${ maxWidth }x${ maxHeight }px and ${ (props.maxSize ?? 0) / 1024 / 1024 }MB`
  }

  const [maxWidth, maxHeight] = props.maxResolution || [Infinity, Infinity]
  return (
    <div className="editable-avatar">
      <div className={classWithModifiers("editable-avatar__container", (pending || props.pending) && "pending")}>
        {image && (
          <img src={image} alt="avatar" className={classWithModifiers("editable-avatar__image", props.contain && "contain")} />
        )}
        {!image && props.firstName && (
          <div ref={letterRef} className={classWithModifiers("editable-avatar__image", props.contain && "contain")} >
            <ColoredLetter letter={props.firstName} letterColor={props.letterColor} backgroundColor={props.backgroundColor} baseHSL={props.baseHSL} />
          </div>
        )}
        <label className={classWithModifiers("editable-avatar__cover", props.showCover && "show")}>
          <Icon className="editable-avatar__icon" name="touch" />
          <input className="editable-avatar__input" name={props.name} type="file" accept=".png, .jpg, .jpeg" onChange={onChange} aria-hidden={false} />
        </label>
        <div className="editable-avatar__loader">
          <Loader />
        </div>
      </div>
      {invalid && (
        <div className="editable-avatar__validity">
          Image cannot be uploaded. {getErrorMessage()}
        </div>
      )}
    </div>
  )
}

export default EditableAvatar
